import SeoFooter from "../../components/SeoFooter";
import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import { 
  Plane, Clock, Shield, Phone, Mail, CheckCircle2, MapPin, Users, Luggage,
  Baby, Star, ArrowRight, ExternalLink, Briefcase, Wifi, Car, BadgeCheck,
  Timer, UserCheck, Volume2, HandHelping
} from "lucide-react";
import ContactPopup from "../../components/ContactPopup";
import PageTransition from "../../components/PageTransition";

const AirportTransfersPage = () => {
  // Vehicle comparison data with luggage capacity
  const vehicles = [
    {
      type: "Executive Sedan",
      passengers: "3",
      largeCases: "2",
      smallBags: "2",
      bestFor: "Solo business travelers & couples",
      image: "🚗"
    },
    {
      type: "Luxury SUV",
      passengers: "4",
      largeCases: "3",
      smallBags: "2",
      bestFor: "Families & extra luggage",
      image: "🚙"
    },
    {
      type: "Premium Van",
      passengers: "7-11",
      largeCases: "10+",
      smallBags: "6+",
      bestFor: "Large groups & corporate teams",
      image: "🚐"
    }
  ];

  // Route times
  const routes = [
    { from: "BNE", to: "Brisbane CBD", time: "~25 mins", price: "From $95" },
    { from: "BNE", to: "Gold Coast", time: "~65 mins", price: "From $195" },
    { from: "OOL", to: "Surfers Paradise", time: "~35 mins", price: "From $85" },
    { from: "BNE", to: "Sunshine Coast", time: "~80 mins", price: "From $245" },
  ];

  // Why chauffeur reasons
  const whyBetter = [
    {
      icon: BadgeCheck,
      title: "Guaranteed Pickup",
      description: "No \"Driver Cancelled\" notifications when you're on a tight schedule."
    },
    {
      icon: Baby,
      title: "Child Safety",
      description: "We provide pre-installed, premium baby seats and boosters (forward or rear-facing)."
    },
    {
      icon: Volume2,
      title: "Quiet Productivity",
      description: "Our vehicles are mobile offices. Enjoy a quiet, climate-controlled environment to take calls or prepare for your meeting."
    },
    {
      icon: HandHelping,
      title: "Assistance with Bags",
      description: "From the arrivals hall to your front door, we handle the heavy lifting."
    }
  ];

  // FAQ items
  const faqItems = [
    {
      question: "Where do I meet my driver?",
      answer: "For domestic flights, your driver will meet you at the baggage carousel. For international flights, we wait at the arrivals hall exit. You will receive your driver's name and mobile number via SMS 30 minutes before landing."
    },
    {
      question: "What if my flight is delayed by 3 hours?",
      answer: "No problem. We track your flight number. Your booking is automatically updated to match your new arrival time at no additional cost."
    },
    {
      question: "Do you offer 4:00 AM pickups?",
      answer: "Yes, we operate 24/7. Early morning airport runs are our specialty. Whether it's a 4AM departure or a midnight arrival, we'll be there."
    },
    {
      question: "What airports do you service?",
      answer: "We provide transfers to and from Brisbane Airport (BNE), Gold Coast Airport (OOL), Sunshine Coast Airport (MCY), and Toowoomba Wellcamp Airport (WTB)."
    },
    {
      question: "Are tolls and airport fees included?",
      answer: "Yes! Unlike rideshare apps, our quoted price includes all tolls and airport parking/pickup fees. No surprises."
    }
  ];

  // The three guarantees
  const guarantees = [
    {
      icon: Plane,
      title: "Real-Time Flight Tracking",
      description: "We monitor your flight status in real-time. If your plane is early or delayed, your chauffeur will be there exactly when you land—no extra waiting fees."
    },
    {
      icon: UserCheck,
      title: "Professional Meet & Greet",
      description: "Your driver will meet you inside the terminal at the designated arrivals area with a personalized name board. No wandering around car parks looking for your ride."
    },
    {
      icon: Shield,
      title: "Fixed Corporate Rates",
      description: "Unlike rideshare apps, we never use surge pricing. The price you are quoted is the price you pay, including all tolls and airport parking fees."
    }
  ];

  // Schema.org data
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Brisbane & Gold Coast Airport Chauffeur Transfers",
    "description": "Premium airport transfer service with flight tracking, meet & greet, and fixed pricing. Brisbane Airport (BNE) and Gold Coast Airport (OOL) transfers.",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Limo Near Me",
      "telephone": "+61481437772",
      "email": "info@limonearme.com.au",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Brisbane",
        "addressRegion": "QLD",
        "addressCountry": "AU"
      }
    },
    "areaServed": [
      { "@type": "Airport", "name": "Brisbane Airport", "iataCode": "BNE" },
      { "@type": "Airport", "name": "Gold Coast Airport", "iataCode": "OOL" },
      { "@type": "City", "name": "Brisbane" },
      { "@type": "City", "name": "Gold Coast" },
      { "@type": "City", "name": "Sunshine Coast" }
    ],
    "serviceType": "Airport Transfer",
    "offers": {
      "@type": "AggregateOffer",
      "lowPrice": "85",
      "highPrice": "295",
      "priceCurrency": "AUD"
    }
  };

  // FAQ Schema
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };

  return (
    <PageTransition>
      <div className="min-h-screen bg-background">
        <Helmet>
          <title>Brisbane Airport Chauffeur | Gold Coast Airport Transfers | BNE & OOL | Limo Near Me</title>
          <meta name="description" content="Premium Brisbane Airport chauffeur service with flight tracking & meet-and-greet. Fixed prices, no surge. Gold Coast Airport transfers. 24/7 service. Book your BNE or OOL transfer today!" />
          <meta name="keywords" content="Brisbane Airport chauffeur, BNE airport transfer, Gold Coast Airport transfer, OOL airport limo, Brisbane Airport limo, airport pickup Brisbane, executive airport transfer, corporate airport chauffeur" />
          <link rel="canonical" href="https://www.limonearme.com.au/services/airport-transfers" />
          <meta property="og:title" content="Brisbane & Gold Coast Airport Chauffeur Transfers | Limo Near Me" />
          <meta property="og:description" content="Premium airport transfers with flight tracking, meet & greet, and fixed pricing. No surge, no surprises. 24/7 service." />
          <meta property="og:type" content="website" />
          <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
          <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        </Helmet>

        <ContactPopup />

        {/* Header */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-black/5">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between h-16">
              <Link to="/" className="flex items-center">
                <img src="/limo-logo.jpg" alt="Limo Near Me - Brisbane Airport Chauffeur" className="h-10 w-auto" />
              </Link>
              <div className="hidden md:flex items-center gap-6">
                <Link to="/" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Home</Link>
                <Link to="/fleet" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Our Fleet</Link>
                <Link to="/services/airport-transfers" className="text-sm text-foreground font-medium">Airport Transfers</Link>
                <Link to="/services/cruise-terminal" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Cruise Terminal</Link>
                <Link to="/corporate-travel" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Corporate</Link>
                <a href="tel:+61481437772" className="flex items-center gap-2 text-primary font-semibold">
                  <Phone className="h-4 w-4" />
                  +61 481 437 772
                </a>
                <Link to="/book">
                  <Button size="sm">Book Now</Button>
                </Link>
              </div>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section className="pt-24 pb-16 bg-gradient-to-b from-sky-50 to-background">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <div className="inline-flex items-center gap-2 bg-sky-100 text-sky-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
                  <Clock className="h-4 w-4" />
                  Available 24/7 - Even 4AM Pickups
                </div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6">
                  Brisbane & Gold Coast Airport Chauffeur Transfers
                </h1>
                <p className="text-xl text-muted-foreground mb-4">
                  <strong>Reliable. Punctual. Premium.</strong> The Executive Alternative to Rideshare.
                </p>
                <p className="text-muted-foreground mb-8">
                  Don't leave your flight connection to chance. Whether you're arriving at <strong>Brisbane Airport (BNE)</strong> or <strong>Gold Coast Airport (OOL)</strong>, Limo Near Me provides a seamless, fixed-price transfer service that bypasses the taxi queues and rideshare cancellations.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link to="/book">
                    <Button size="lg" className="w-full sm:w-auto">
                      <Plane className="h-5 w-5 mr-2" />
                      Book Now
                    </Button>
                  </Link>
                  <Link to="/contact">
                    <Button size="lg" variant="outline" className="w-full sm:w-auto">
                      Get Instant Quote
                    </Button>
                  </Link>
                </div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                className="relative"
              >
                <img 
                  src="https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=600&h=400&fit=crop" 
                  alt="Professional chauffeur with name sign at Brisbane Airport arrivals" 
                  className="rounded-2xl shadow-2xl"
                />
                <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                      <CheckCircle2 className="h-6 w-6 text-green-600" />
                    </div>
                    <div>
                      <p className="font-semibold">Flight Tracked</p>
                      <p className="text-sm text-muted-foreground">We adjust to delays</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* The Three Guarantees */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">The "Limo Near Me" Airport Guarantee</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                We understand that airport travel is time-sensitive. Our service is built around three core pillars:
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {guarantees.map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gradient-to-br from-sky-50 to-white rounded-xl p-8 border border-sky-100 text-center"
                >
                  <div className="w-16 h-16 bg-sky-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <item.icon className="h-8 w-8 text-sky-600" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                  <p className="text-muted-foreground">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Vehicle Selection Table */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">Choose Your Airport Executive Vehicle</h2>
              <p className="text-muted-foreground">
                To ensure a comfortable ride, please select a vehicle that matches your group size and luggage requirements:
              </p>
            </div>

            {/* Desktop Table */}
            <div className="hidden md:block overflow-hidden rounded-xl border border-black/10 bg-white">
              <table className="w-full">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-6 py-4 text-left font-semibold">Vehicle Type</th>
                    <th className="px-6 py-4 text-center font-semibold">
                      <div className="flex items-center justify-center gap-2">
                        <Users className="h-4 w-4" /> Passengers
                      </div>
                    </th>
                    <th className="px-6 py-4 text-center font-semibold">
                      <div className="flex items-center justify-center gap-2">
                        <Luggage className="h-4 w-4" /> Large Cases
                      </div>
                    </th>
                    <th className="px-6 py-4 text-center font-semibold">
                      <div className="flex items-center justify-center gap-2">
                        <Briefcase className="h-4 w-4" /> Carry-ons
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left font-semibold">Best For...</th>
                  </tr>
                </thead>
                <tbody>
                  {vehicles.map((vehicle, index) => (
                    <tr key={vehicle.type} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                      <td className="px-6 py-4 font-semibold">{vehicle.type}</td>
                      <td className="px-6 py-4 text-center text-lg">{vehicle.passengers}</td>
                      <td className="px-6 py-4 text-center text-lg">{vehicle.largeCases}</td>
                      <td className="px-6 py-4 text-center text-lg">{vehicle.smallBags}</td>
                      <td className="px-6 py-4 text-muted-foreground">{vehicle.bestFor}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden space-y-4">
              {vehicles.map((vehicle) => (
                <div key={vehicle.type} className="bg-white rounded-xl p-6 border border-black/10">
                  <h3 className="font-bold text-lg mb-4">{vehicle.type}</h3>
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="text-center">
                      <Users className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
                      <p className="text-xl font-bold">{vehicle.passengers}</p>
                      <p className="text-xs text-muted-foreground">Passengers</p>
                    </div>
                    <div className="text-center">
                      <Luggage className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
                      <p className="text-xl font-bold">{vehicle.largeCases}</p>
                      <p className="text-xs text-muted-foreground">Large Cases</p>
                    </div>
                    <div className="text-center">
                      <Briefcase className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
                      <p className="text-xl font-bold">{vehicle.smallBags}</p>
                      <p className="text-xs text-muted-foreground">Carry-ons</p>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">{vehicle.bestFor}</p>
                </div>
              ))}
            </div>

            <div className="text-center mt-8">
              <Link to="/fleet">
                <Button variant="outline">
                  View Full Fleet Details
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Route Times & Prices */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">24/7 Transfers Across South East Queensland</h2>
              <p className="text-muted-foreground">
                We provide door-to-door service between all major airports and regional hubs
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {routes.map((route, index) => (
                <motion.div
                  key={`${route.from}-${route.to}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 border border-black/5 hover:shadow-lg transition-all"
                >
                  <div className="flex items-center gap-2 mb-4">
                    <span className="bg-sky-100 text-sky-700 px-2 py-1 rounded font-mono text-sm font-bold">{route.from}</span>
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                    <span className="font-semibold">{route.to}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Clock className="h-4 w-4" />
                      {route.time}
                    </div>
                    <Link to="/book"><Button size="sm" variant="outline" className="text-xs rounded-full">Get Price</Button></Link>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Flight Status Link */}
            <div className="mt-12 bg-sky-50 rounded-xl p-6 flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-sky-100 rounded-full flex items-center justify-center">
                  <Plane className="h-6 w-6 text-sky-600" />
                </div>
                <div>
                  <h4 className="font-bold">Check Your Flight Status</h4>
                  <p className="text-sm text-muted-foreground">Verify your arrival time before booking</p>
                </div>
              </div>
              <div className="flex gap-3">
                <a 
                  href="https://www.bne.com.au/passenger/flight-information/arrivals" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-white border border-sky-200 px-4 py-2 rounded-lg text-sm font-medium hover:bg-sky-50 transition-colors"
                >
                  BNE Flights
                  <ExternalLink className="h-4 w-4" />
                </a>
                <a 
                  href="https://www.goldcoastairport.com.au/flight-information/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-white border border-sky-200 px-4 py-2 rounded-lg text-sm font-medium hover:bg-sky-50 transition-colors"
                >
                  OOL Flights
                  <ExternalLink className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Why Chauffeur is Better */}
        <section className="py-16 bg-gray-900 text-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold mb-4">Why Chauffeur is Better Than Rideshare in 2026</h2>
              <p className="text-gray-400">
                When reliability matters, choose professional over "gig economy"
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {whyBetter.map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gray-800 rounded-xl p-6"
                >
                  <div className="w-12 h-12 bg-sky-500/20 rounded-xl flex items-center justify-center mb-4">
                    <item.icon className="h-6 w-6 text-sky-400" />
                  </div>
                  <h3 className="font-bold text-lg mb-2">{item.title}</h3>
                  <p className="text-gray-400 text-sm">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">Frequently Asked Questions</h2>
              <p className="text-muted-foreground">
                Everything you need to know about our airport transfer service
              </p>
            </div>

            <div className="space-y-4">
              {faqItems.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gray-50 rounded-xl p-6"
                >
                  <h3 className="font-semibold text-lg mb-3">{faq.question}</h3>
                  <p className="text-muted-foreground">{faq.answer}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-primary text-primary-foreground">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <Plane className="h-16 w-16 mx-auto mb-6 opacity-80" />
            <h2 className="text-2xl md:text-3xl font-bold mb-4">Secure Your Airport Transfer</h2>
            <p className="text-primary-foreground/80 mb-8 text-lg">
              Don't leave your important journey to chance. Book your Brisbane or Gold Coast airport transfer today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/book">
                <Button size="lg" className="w-full sm:w-auto px-8 bg-white text-primary hover:bg-gray-100">
                  Get Instant Quote
                </Button>
              </Link>
              <Link to="/book">
                <Button size="lg" variant="secondary" className="w-full sm:w-auto px-8">
                  Book Now
                </Button>
              </Link>
            </div>
            <p className="mt-8 text-sm text-primary-foreground/70">
              Questions? Call us 24/7 at{" "}
              <a href="tel:+61481437772" className="underline font-semibold">+61 481 437 772</a>
            </p>
          </div>
        </section>

        {/* Footer */}
      <SeoFooter />
      </div>
    </PageTransition>
  );
};

export default AirportTransfersPage;
